/**
 * Detects non-actionable Safari TypeError noise from the Datadog Browser SDK
 * internals where minified code accesses `.sdkVersion` on an undefined
 * configuration object.
 *
 * This occurs due to a version mismatch between `@datadog/browser-core`
 * instances: `@datadog/browser-rum` (6.32.0) and
 * `@datadog/openfeature-browser` (^6.33.0) resolve separate copies of
 * browser-core, and certain internal code paths (e.g. `buildTags`) can
 * receive an undefined configuration. Safari reports this as:
 *   "undefined is not an object (evaluating 't.sdkVersion')"
 *
 * The error has no useful stack trace and is not actionable from application
 * code. We suppress it only when the stack contains no meaningful frames.
 */
export function isDatadogSdkInternalNoise(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  if (!message) {
    return false;
  }

  if (!message.includes("sdkVersion")) {
    return false;
  }

  const isSafariUndefinedAccess =
    message.includes("undefined is not an object") ||
    message.includes("null is not an object");

  if (!isSafariUndefinedAccess) {
    return false;
  }

  if (!stack || stack === `TypeError: ${message}`) {
    return true;
  }

  const frames = stack.split("\n").filter((l) => l.trim().startsWith("at "));
  return frames.length === 0;
}
